import {
  ColDef,
  FirstDataRenderedEvent,
  SelectionChangedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import {
  CELL_CLASSES,
  TableContainer,
  TableContent,
  TableHeader,
  TextRenderer,
  ROW_SELECTION,
  TABLE_LAYOUT_OPTIONS,
} from "@cdkglobal/radial";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-balham.css";
import { useMemo, useRef, useState } from "react";
import { GenericSearchResponseObject } from "../../../../../types";
import { useIntl } from "react-intl";

export default function CopyPOUnitsOfMeasureTable({
  headerTitle,
  responseRowData,
  setSelectedRows,
  selectedRows,
}: Readonly<{
  headerTitle: string;
  responseRowData: GenericSearchResponseObject[];
  setSelectedRows: (rows: GenericSearchResponseObject[]) => void;
  selectedRows: GenericSearchResponseObject[];
}>): JSX.Element {
  const gridRef = useRef<AgGridReact>(null);
  const [searchInputValue, setSearchInputValue] = useState("");
  const intl = useIntl();
  const handleSelectionChange = (event: SelectionChangedEvent) => {
    const newselectedRows = event.api.getSelectedRows();
    setSelectedRows(newselectedRows);
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInputValue(e.target.value);
  };

  const onFirstDataRendered = (event: FirstDataRenderedEvent) => {
    event.api.forEachNode((node) => {
      if (selectedRows.length && selectedRows.includes(node.data)) {
        node.setSelected(true);
      }
    });
  };

  const columns: ColDef[] = useMemo(
    () => [
      {
        cellClass: CELL_CLASSES.CHECKBOX,
        field: "check",
        filter: false,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        headerClass: [CELL_CLASSES.CHECKBOX, CELL_CLASSES.SELECT_ALL],
        headerName: "",
        maxWidth: 55,
        minWidth: 55,
      },
      {
        cellRenderer: TextRenderer,
        field: "uom_cd",
        headerName: intl.formatMessage({
          id: "label.unitOfMeasureCode",
          defaultMessage: "Unit of Measure Code",
        }),
      },
      {
        cellRenderer: TextRenderer,
        field: "uom_desc",
        headerName: intl.formatMessage({
          id: "label.unitOfMeasureDescription",
          defaultMessage: "Unit of Measure Description",
        }),
        minWidth: 450,
      },
    ],
    [],
  );

  return (
    <TableContainer agGridVersion="31.3.4">
      <TableHeader
        className="copy-dialog-table-header"
        id="copy-dialog-table-header"
        dataTestId="copy-dialog-table-header"
        hideBorder
        hideTableTitle={false}
        isAlwaysExpanded={false}
        headerTitle={headerTitle}
        isDownloadable={false}
        isPrintable={false}
        isSearchable={true}
        searchInputValue={searchInputValue}
        onSearchInputChange={handleSearchInputChange}
        onSearchInputClear={() => setSearchInputValue("")}
        searchInputName="copy-dialog-table-search-input-name"
        searchPlaceholder="Search all columns"
        overflowButtonProps={undefined}
      />
      <TableContent data-testid="table-content">
        <AgGridReact
          className="ag-theme-balham"
          containerStyle={{ height: "45vh", width: "100%" }}
          rowData={responseRowData}
          quickFilterText={searchInputValue}
          columnDefs={columns}
          domLayout={TABLE_LAYOUT_OPTIONS.NORMAL}
          rowSelection={ROW_SELECTION.MULTIPLE}
          suppressRowClickSelection
          defaultColDef={{
            filter: true,
            sortable: true,
            resizable: true,
            flex: 1,
            autoHeight: true,
            wrapText: true,
          }}
          ref={gridRef}
          onSelectionChanged={handleSelectionChange}
          onFirstDataRendered={onFirstDataRendered}
        />
      </TableContent>
    </TableContainer>
  );
}
